import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase, SITE_URL } from '../lib/supabase';
import { checkWorkEmail } from '../lib/workEmail';
import { useAuth } from '../contexts/AuthContext';
import { TEAM_OPTIONS } from '../lib/database.types';

const PENDING_KEY = 'ga_pending_signup';

export default function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [team, setTeam] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [params] = useSearchParams();
  const next = params.get('next') || '/locations';
  const navigate = useNavigate();
  const { session, loading } = useAuth();

  useEffect(() => {
    if (!loading && session) navigate(next, { replace: true });
  }, [session, loading, navigate, next]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setWarning(null);

    const check = checkWorkEmail(email);
    if (!check.ok) {
      if (check.reason === 'shape') {
        setError("That doesn't look like an email address.");
        return;
      }
      setWarning(
        `Goaround is built for teams. Please use your work email, not @${check.domain}. If you're a bar owner, list your spot from the For Restaurants page instead.`,
      );
      return;
    }

    setSubmitting(true);

    // Stash so we can apply to profile after the magic link verifies (the
    // trigger uses raw_user_meta_data on initial signup; this covers returning
    // users who add details for the first time).
    sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ first_name: firstName.trim(), last_name: lastName.trim(), team }),
    );

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          team,
          full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        },
        emailRedirectTo: `${SITE_URL}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    setSubmitting(false);
    if (error) {
      setError(error.message);
      return;
    }
    setSent(true);
  }

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 48, paddingBottom: 96 }}>
        <div className="container">
          <div className="r-split">
            <div>
              <div className="eyebrow" style={{ marginBottom: 18 }}>Sign up</div>
              <h1 className="display" style={{ fontSize: 'clamp(40px, 7vw, 80px)', margin: 0, lineHeight: 0.95 }}>
                Create an account to <span className="serif-italic" style={{ color: 'var(--wine)' }}>see offers.</span>
              </h1>

              {sent ? (
                <div style={{ marginTop: 32, padding: 24, borderRadius: 18, background: 'var(--card)' }}>
                  <div className="display" style={{ fontSize: 26, marginBottom: 8 }}>Check your inbox.</div>
                  <p style={{ color: 'var(--ink-soft)', margin: 0 }}>
                    Magic link sent to <strong>{email}</strong>. It expires in an hour.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} style={{ marginTop: 32 }}>
                  <div className="stack">
                    <div className="r-grid-2" style={{ gap: 16 }}>
                      <label>
                        <span className="field-label">First name</span>
                        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
                      </label>
                      <label>
                        <span className="field-label">Last name</span>
                        <input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                      </label>
                    </div>
                    <label>
                      <span className="field-label">Work email</span>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourcompany.com" required />
                      <div className="field-hint">
                        We roll up bookings by company, so use the same email your team uses for work.
                      </div>
                    </label>
                    <label>
                      <span className="field-label">Team</span>
                      <select value={team} onChange={(e) => setTeam(e.target.value)} required>
                        <option value="">Pick one</option>
                        {TEAM_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    {error && <div className="field-error">{error}</div>}
                    {warning && <div className="field-error">{warning}</div>}
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={submitting} style={{ width: '100%', justifyContent: 'center', marginTop: 24 }}>
                    {submitting ? 'Sending magic link...' : 'Email me a magic link →'}
                  </button>
                </form>
              )}

              <p className="muted" style={{ fontSize: 13, marginTop: 24 }}>
                Already have an account? <Link to="/login" style={{ textDecoration: 'underline' }}>Sign in</Link>.
              </p>
            </div>

            <aside style={{ position: 'sticky', top: 24, alignSelf: 'start' }}>
              <div style={{ padding: 28, borderRadius: 20, background: 'var(--ink)', color: 'var(--bg)' }}>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 12 }}>What you unlock</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <Bullet n="01" t="Pre-paid packages" b="Reserve a happy hour with a single tap. Your card runs once, up front." />
                  <Bullet n="02" t="One link, real RSVPs" b="Drop a link in Slack. Teammates RSVP without making accounts." />
                  <Bullet n="03" t="One receipt, never split" b="Finance gets one clean line item. Your team never sees a check." />
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

function Bullet({ n, t, b }: { n: string; t: string; b: string }) {
  return (
    <li style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 14, alignItems: 'start' }}>
      <span className="display" style={{ fontSize: 22, color: 'var(--primary)', lineHeight: 1 }}>{n}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{t}</div>
        <div style={{ fontSize: 13, color: 'color-mix(in oklch, var(--bg) 65%, transparent)', marginTop: 4, lineHeight: 1.45 }}>{b}</div>
      </div>
    </li>
  );
}
